import { useEffect, useState, useCallback } from "react";
import { Scale, Landmark, Building2, X } from "lucide-react";

const PROFESSIONS = [
  { label: "An attorney", icon: Scale },
  { label: "A qualified intermediary", icon: Landmark },
  { label: "A wealth management firm", icon: Building2 },
];

const STATES = [
  "California", "Texas", "Florida", "New York", "Illinois", "Arizona", "Colorado",
  "Washington", "Georgia", "North Carolina", "Massachusetts", "Nevada", "Ohio",
  "Pennsylvania", "Virginia", "Tennessee", "Oregon", "New Jersey", "Utah", "Minnesota",
];

const pick = (arr, not) => {
  let v;
  do { v = arr[Math.floor(Math.random() * arr.length)]; } while (not !== undefined && v === not);
  return v;
};

function makeTimeAgo() {
  // Weighted mix of hours and days to feel organic (no "minutes ago").
  const r = Math.random();
  if (r < 0.55) {
    const h = Math.floor(Math.random() * 22) + 2; // 2–23 hours
    return `${h} hours ago`;
  }
  const d = Math.floor(Math.random() * 6) + 1; // 1–6 days
  return d === 1 ? "1 day ago" : `${d} days ago`;
}

function makeEvent(prevState) {
  const prof = PROFESSIONS[Math.floor(Math.random() * PROFESSIONS.length)];
  const state = pick(STATES, prevState);
  return { prof, state, timeAgo: makeTimeAgo() };
}

export function SocialProofToast() {
  const [event, setEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const cycle = useCallback((prevState) => {
    const e = makeEvent(prevState);
    setEvent(e);
    setVisible(true);
    const hide = setTimeout(() => setVisible(false), 6000);
    return { e, hide };
  }, []);

  useEffect(() => {
    if (dismissed) return;
    let hideTimer;
    let nextTimer;
    const first = setTimeout(() => {
      let prev;
      const run = () => {
        const { e, hide } = cycle(prev);
        prev = e.state;
        hideTimer = hide;
        nextTimer = setTimeout(run, 9000); // 6s visible + 3s gap
      };
      run();
    }, 3500);
    return () => { clearTimeout(first); clearTimeout(hideTimer); clearTimeout(nextTimer); };
  }, [cycle, dismissed]);

  if (dismissed || !event) return null;

  const Icon = event.prof.icon;

  return (
    <div
      data-testid="social-proof-toast"
      className={`fixed bottom-5 left-5 z-40 max-w-[330px] transition-all duration-500 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      <div className="relative bg-white text-ally-ink rounded-2xl shadow-2xl shadow-black/30 border border-black/5 p-4 pr-9 flex items-start gap-3">
        <button
          data-testid="social-proof-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="absolute top-2.5 right-2.5 text-ally-slate/50 hover:text-ally-ink transition-colors"
        >
          <X size={15} />
        </button>
        <div className="w-11 h-11 rounded-xl bg-ally-soft flex items-center justify-center text-ally-pink shrink-0">
          <Icon size={20} />
        </div>
        <div className="min-w-0">
          <p className="text-sm leading-snug">
            <span className="font-bold">{event.prof.label}</span> in{" "}
            <span className="font-bold">{event.state}</span> booked a demo walkthrough.
          </p>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
            <span className="text-xs text-ally-slate">{event.timeAgo}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
